import { Link } from "react-router-dom";

const CONTROL_ROWS: { area: string; implemented: string; roadmap: string }[] = [
	{
		area: "Content Security Policy (SPA shell)",
		implemented: "Baseline meta CSP on the static shell; restrictive script policy; connect-src allows same-origin API and ws/wss.",
		roadmap: "Nonces/hashes if inline scripts grow; extend connect-src/frame-src when Stripe or other third parties load.",
	},
	{
		area: "CORS / same-origin API",
		implemented: "Primary HTTP + WebSocket are same-origin to the deployed app; no broad wildcard for credentialed routes.",
		roadmap: "Explicit allowlists per provider when OAuth/integration callbacks land.",
	},
	{
		area: "Curator session cookie (wi_curator)",
		implemented: "HttpOnly, Secure in production, SameSite=Lax; HMAC token; constant-time verification; bounded parse sizes.",
		roadmap: "Cross-site embedding review if widgets ever ship.",
	},
	{
		area: "Curator Link storage",
		implemented: "SHA-256 hash of Curator Link credential in D1; verification uses constant-time compare; raw link secret not persisted server-side.",
		roadmap: "Short-lived delegated tokens where hosts allow.",
	},
	{
		area: "Internal D1 bridge",
		implemented:
			"POST /api/internal/d1/* requires X-WorthyIdea-Internal matching INTERNAL_D1_SECRET (constant-time); room binding headers capped.",
		roadmap: "Dual-secret overlap window only if explicitly implemented.",
	},
	{
		area: "Durable Object role trust",
		implemented: "Worker sets trusted role; DO does not trust client-sent role fields for privileged actions; hello/auth handshake.",
		roadmap: "Continue tightening as protocol evolves.",
	},
	{
		area: "Webhook ingress (/api/webhooks/*)",
		implemented: "Stripe and future providers: verify when wired with secrets; minimal logging posture documented.",
		roadmap: "Provider MAC, replay/idempotency table, tenant binding from verified claims only, safe retry semantics.",
	},
	{
		area: "Operational telemetry",
		implemented: "Structured logs avoid question/payload bodies; room id + phase preferred at info level.",
		roadmap: "Org-level health metrics without ballot prose (Addendum V pattern); CRM correlation isolated from vote store.",
	},
	{
		area: "SOC 2–style assurance",
		implemented: "Engineering hygiene and documentation; no certification claimed in-repo.",
		roadmap: "RBAC on dashboards, audit trails for admin/billing, DPAs/vendor review backlog — alignment posture only until an audit program exists.",
	},
];

export function SecurityPage() {
	return (
		<div className="wi-editorial-prose space-y-10 pb-24">
			<header className="space-y-4">
				<p className="font-geist text-wi-text/50 text-xs uppercase tracking-[0.16em]">Security &amp; trust</p>
				<h1 className="font-newsreader text-wi-text text-[2rem] leading-[1.1] tracking-tight sm:text-[2.65rem]">
					How we think about safety for the poll
				</h1>
				<div className="space-y-3 font-geist text-[0.9rem] leading-relaxed text-wi-text/78 sm:text-[0.9375rem]">
					<p>
						WorthyIdea is a small surface area on purpose: explicit routes, typed payloads, and a tight boundary between
						public voters and Curator capability. This page is the public{" "}
						<strong className="text-wi-text">controls matrix</strong> — what is{" "}
						<strong className="text-wi-text">implemented today</strong> versus what remains on the{" "}
						<strong className="text-wi-text">roadmap</strong>.
					</p>
					<p className="rounded-lg border border-wi-gold/20 bg-wi-surface/80 px-3 py-2.5 text-[0.85rem] text-wi-text/75">
						We do <strong className="text-wi-text">not</strong> claim SOC 2 certification or compliance. WorthyIdea is built
						with controls aligned to SOC 2 Trust Services Criteria — happy to walk through the architecture with your security
						team. That is not an audit opinion.
					</p>
					<p>
						See also{" "}
						<Link to="/privacy" className="font-medium text-wi-gold underline-offset-2 hover:underline">
							Privacy
						</Link>{" "}
						for data lifecycle framing.
					</p>
				</div>
			</header>

			<section id="enterprise-trust" className="space-y-4 scroll-mt-8">
				<p className="font-geist text-wi-text/50 text-xs uppercase tracking-[0.16em]">Enterprise narrative</p>
				<h2 className="font-newsreader text-wi-text text-xl tracking-tight sm:text-2xl">
					Architecture story lives in the Trust Center
				</h2>
				<p className="font-geist text-[0.9rem] leading-relaxed text-wi-text/78 sm:text-[0.9375rem]">
					This page is the engineering <strong className="text-wi-text">controls matrix</strong> (what is implemented
					vs roadmap). For the public Trust Center narrative — architecture, encryption, minimization, and disclosure —
					see{" "}
					<Link to="/trust/security" className="font-medium text-wi-gold underline-offset-2 hover:underline">
						Trust Center · Security
					</Link>
					. Host-native flows that write a <strong className="text-wi-text">Consensus Record</strong> and clear temporary
					session memory are documented there; this consumer site still keeps ballots in D1 for the room TTL so Curator
					Links can recover.
				</p>
			</section>

			<section className="overflow-x-auto rounded-xl border border-wi-text/10 shadow-wi-soft">
				<table className="w-full min-w-[32rem] border-collapse text-left font-geist text-sm">
					<thead>
						<tr className="border-wi-text/10 border-b bg-wi-surface/90">
							<th className="px-4 py-3 font-newsreader text-base font-normal tracking-tight text-wi-text">Area</th>
							<th className="px-4 py-3 font-newsreader text-base font-normal tracking-tight text-wi-text">Implemented</th>
							<th className="px-4 py-3 font-newsreader text-base font-normal tracking-tight text-wi-text">Roadmap / next</th>
						</tr>
					</thead>
					<tbody className="divide-wi-text/8 divide-y bg-wi-surface/50">
						{CONTROL_ROWS.map((row) => (
							<tr key={row.area} className="align-top">
								<td className="px-4 py-3.5 font-medium text-wi-text">{row.area}</td>
								<td className="px-4 py-3.5 text-wi-text/78">{row.implemented}</td>
								<td className="px-4 py-3.5 text-wi-text/72">{row.roadmap}</td>
							</tr>
						))}
					</tbody>
				</table>
			</section>

			<section className="space-y-3">
				<h2 className="font-newsreader text-wi-text text-xl tracking-tight sm:text-2xl">Public documentation</h2>
				<p className="font-geist text-[0.875rem] leading-relaxed text-wi-text/70">
					Operator-only engineering docs stay private. Visitors get substance on these public surfaces:
				</p>
				<ul className="space-y-1.5 font-geist text-sm text-wi-text/85">
					<li>
						<Link to="/gears" className="font-medium text-wi-gold underline-offset-2 hover:underline">
							Gears
						</Link>{" "}
						— Workers, Durable Objects, D1, and trust borders.
					</li>
					<li>
						<Link to="/trust/security" className="font-medium text-wi-gold underline-offset-2 hover:underline">
							Trust Center · Security
						</Link>{" "}
						— encryption, minimization, disclosure.
					</li>
					<li>
						<Link to="/help/admin" className="font-medium text-wi-gold underline-offset-2 hover:underline">
							Admin FAQ
						</Link>{" "}
						— including what is (and is not) published for operators.
					</li>
				</ul>
			</section>
		</div>
	);
}
